import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Search, Store } from "lucide-react";

const resolveImg = (img) => (img?.startsWith("http") ? img : `${API}/files/${img}`);

export default function Shops() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = term ? `?search=${encodeURIComponent(term)}` : "";
    api.get(`/shops${params}`).then(({ data }) => {
      setShops(data.shops || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [term]);

  const onSearch = (e) => {
    e.preventDefault();
    setTerm(q.trim());
  };

  return (
    <div data-testid="shops-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ The Makers</div>
      <h1 data-testid="shops-heading" className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
        Browse our<br /><em className="text-primary not-italic">makers</em>.
      </h1>
      <p className="text-muted-foreground mt-5 max-w-md text-sm leading-relaxed">
        Search for a shop by name and step inside — every store is an independent, female-owned handmade brand.
      </p>

      {/* Shop-name search */}
      <form
        onSubmit={onSearch}
        data-testid="shop-search-form"
        className="mt-10 flex items-center gap-3 border-b-2 border-border focus-within:border-primary max-w-xl transition-colors"
      >
        <Search className="w-5 h-5 text-muted-foreground" />
        <input
          data-testid="shop-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search shops by name…"
          className="bg-transparent outline-none text-base py-3.5 flex-1 placeholder:text-muted-foreground"
        />
        {term && (
          <button
            type="button"
            data-testid="shop-search-clear"
            onClick={() => { setQ(""); setTerm(""); }}
            className="text-xs uppercase tracking-wider text-muted-foreground hover:text-primary"
          >
            Clear
          </button>
        )}
        <button data-testid="shop-search-submit" type="submit" className="text-xs uppercase tracking-wider text-primary font-semibold pr-1">
          Search
        </button>
      </form>

      {/* Results */}
      <div className="mt-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-64 bg-secondary animate-pulse rounded-sm" />
            ))}
          </div>
        ) : shops.length === 0 ? (
          <div data-testid="shops-empty" className="text-muted-foreground text-center py-24 italic">
            {term ? <>No shops match “{term}”.</> : <>No shops to show yet.</>}
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-6">{shops.length} {shops.length === 1 ? "shop" : "shops"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {shops.map((s) => (
                <Link
                  key={s.id}
                  to={`/shop/${s.id}`}
                  data-testid={`shop-card-${s.id}`}
                  className="group border border-border rounded-sm overflow-hidden bg-card hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="h-28 bg-secondary relative overflow-hidden">
                    {s.banner ? (
                      <img src={resolveImg(s.banner)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-secondary to-muted" />
                    )}
                    {s.is_founding && (
                      <span className="founding-badge absolute top-3 left-3 text-[9px] tracking-[0.1em] uppercase font-semibold px-2 py-0.5 rounded-sm">
                        Founding
                      </span>
                    )}
                  </div>
                  <div className="px-5 pb-6 -mt-8">
                    <div className="w-16 h-16 rounded-full border-4 border-card bg-secondary overflow-hidden flex items-center justify-center">
                      {s.logo ? (
                        <img src={resolveImg(s.logo)} alt={s.display_name} className="w-full h-full object-cover" />
                      ) : (
                        <Store className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    <h2 className="font-serif text-2xl tracking-tight mt-4 group-hover:text-primary transition-colors">{s.display_name}</h2>
                    {s.bio && <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">{s.bio}</p>}
                    <div className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground mt-4 font-medium">
                      {s.product_count} {s.product_count === 1 ? "piece" : "pieces"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
